import {
  DataModelAttribute,
  DataModelAttributeType,
  DataModelEntityType,
  DataModelRef,
  DataModelUiEntity,
} from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { DataModelPreview } from '@flowx/designer/shared/entities/data-model.entity'

export interface GeneratedDataModelField {
  fn: string
  ft: string
  fk?: string
  fd?: string
  // Example value: a scalar string for leaf attributes; the generator emits a JSON object
  // (usually `{}`) for OBJECT/reference attributes, where an example is meaningless.
  fe?: string | Record<string, unknown>
  fa?: boolean
  fs?: boolean
  fr?: boolean
}

export interface GeneratedDataModelEntity {
  en: string
  ec: string
  ed?: string
  fl: GeneratedDataModelField[]
}

export interface GeneratedDataModel {
  entities: GeneratedDataModelEntity[]
  root: GeneratedDataModelField[]
}

/**
 * The verbose data-model schema the architect also emits (notably for extend_data_model) — the same
 * shape the FE sends as DATA_MODEL_CONTEXT: spelled-out field names, a `properties` list, and
 * entity-name references. Normalized to {@link GeneratedDataModel} before parsing.
 */
export interface VerboseDataModelProperty {
  name: string
  type: string
  description?: string
  exampleValue?: string | Record<string, unknown>
  isArray?: boolean
  reference?: string
}

export interface VerboseDataModelEntity {
  name: string
  description?: string
  properties: VerboseDataModelProperty[]
}

export interface VerboseDataModel {
  entities: VerboseDataModelEntity[]
  root?: VerboseDataModelProperty[]
}

type ReferenceTarget = Partial<
  Pick<DataModelRef, 'targetFlowxUuid' | 'targetResourceDefinitionId' | 'appId'>
>

const syntheticId = (code: string): string => `generated:${code}`

const toCode = (name: string): string =>
  name
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toUpperCase()

const mapFieldType = (ft: string): DataModelAttributeType =>
  ft === 'CURRENCY' ? DataModelAttributeType.STRING : (ft as DataModelAttributeType)

const verbosePropertyToField = (property: VerboseDataModelProperty): GeneratedDataModelField => ({
  fn: property.name,
  ft: property.type,
  fk: property.reference,
  fd: property.description,
  fe: property.exampleValue,
  fa: property.isArray,
})

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isVerboseEntity = (
  entity: GeneratedDataModelEntity | VerboseDataModelEntity
): entity is VerboseDataModelEntity =>
  Array.isArray((entity as VerboseDataModelEntity).properties)

const isVerboseField = (
  field: GeneratedDataModelField | VerboseDataModelProperty
): field is VerboseDataModelProperty => !('fn' in field)

// The architect emits either the abbreviated (en/ec/fl) or the verbose (name/properties) schema;
// collapse both to the abbreviated shape so the rest of the parser has a single format to handle.
const toGeneratedDataModel = (model: GeneratedDataModel | VerboseDataModel): GeneratedDataModel => ({
  entities: (model?.entities ?? []).filter(isRecord).map((entity) =>
    isVerboseEntity(entity)
      ? {
          en: entity.name,
          ec: toCode(entity.name),
          ed: entity.description,
          fl: (entity.properties ?? []).filter(isRecord).map(verbosePropertyToField),
        }
      : entity
  ),
  root: (model?.root ?? []).filter(isRecord).map((field) =>
    isVerboseField(field) ? verbosePropertyToField(field) : field
  ),
})

const referenceToEntity = (entity: DataModelUiEntity): ReferenceTarget =>
  entity.resourceDefinitionId
    ? { targetResourceDefinitionId: entity.resourceDefinitionId, appId: entity.appInfo?.appId }
    : { targetFlowxUuid: entity.flowxUuid }

export function parseGeneratedDataModel(
  model: GeneratedDataModel | VerboseDataModel,
  resourceType: DataModelEntityType,
  existingEntities: DataModelUiEntity[] = []
): DataModelPreview {
  const generatedDataModel = toGeneratedDataModel(model)
  const isProject = resourceType === DataModelEntityType.PROJECT
  const generatedEntities = generatedDataModel.entities.filter((entity) => !!entity.ec)

  const templateAppInfo = existingEntities.find((e) => e.appInfo?.isCurrentApp)?.appInfo
  const currentAppId = templateAppInfo?.appId

  const existingByName = new Map(existingEntities.map((e) => [e.name, e]))

  const referenceToNew = (code: string): ReferenceTarget =>
    isProject
      ? { targetResourceDefinitionId: syntheticId(code), appId: currentAppId }
      : { targetFlowxUuid: syntheticId(code) }

  const targetByCode = new Map<string, ReferenceTarget>()
  const addCodes = (codes: string[], target: ReferenceTarget): void => {
    codes.filter(Boolean).forEach((code) => targetByCode.set(code, target))
  }
  existingEntities.forEach((e) => addCodes([e.name, toCode(e.name)], referenceToEntity(e)))
  generatedEntities.forEach((g) => {
    const existing = existingByName.get(g.en)
    addCodes(
      [g.ec, g.en, toCode(g.en)],
      existing ? referenceToEntity(existing) : referenceToNew(g.ec)
    )
  })

  const mapField = (field: GeneratedDataModelField, parentCode: string): DataModelAttribute => {
    const type = mapFieldType(field.ft)
    const id = `${syntheticId(parentCode)}:${field.fn}`
    const target = field.fk ? targetByCode.get(field.fk) : undefined

    return {
      id,
      flowxUuid: id,
      name: field.fn,
      type,
      multiInstance: !!field.fa,
      description: field.fd,
      exampleValue: typeof field.fe === 'string' ? field.fe : undefined,
      isSensitive: !!field.fs,
      useInReporting: !!field.fr,
      ...(type === DataModelAttributeType.OBJECT && target
        ? { reference: { id: `${id}:ref`, ...target } as DataModelRef }
        : {}),
    }
  }

  const makeEntity = (entity: GeneratedDataModelEntity): DataModelUiEntity => {
    const attributes = (entity.fl ?? [])
      .filter((field) => field.fn)
      .map((field) => mapField(field, entity.ec))

    if (isProject) {
      return {
        type: DataModelEntityType.PROJECT,
        identifier: syntheticId(entity.ec),
        resourceId: syntheticId(entity.ec),
        resourceDefinitionId: syntheticId(entity.ec),
        name: entity.en,
        description: entity.ed,
        appInfo: { ...templateAppInfo, isCurrentApp: true },
        attributes,
      } as unknown as DataModelUiEntity
    }

    return {
      type: resourceType,
      identifier: syntheticId(entity.ec),
      flowxUuid: syntheticId(entity.ec),
      name: entity.en,
      description: entity.ed,
      root: false,
      attributes,
    } as unknown as DataModelUiEntity
  }

  return {
    entities: generatedEntities.map(makeEntity),
    rootAttributes: (generatedDataModel?.root ?? [])
      .filter((field) => field.fn)
      .map((field) => mapField(field, 'root')),
  }
}
